import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { Bell, Menu, ChevronDown, User, LogOut, Settings, Home, ShoppingCart } from 'lucide-react';
import { SITE_HOME } from '../../config/siteLinks';

const formatMoney = (n) => `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Admin-only cart widget: mirrors the Billing page's cart so an admin can see
// what they've added ("10 on Class 6-8 + 10 on Class 9-10 + ...") from
// anywhere in the app, without navigating away or reloading.
const CartWidget = () => {
  const { isAdmin, cartItems, cartSubtotal, cartTotal, discountPercent } = useCart();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!isAdmin) return null;

  const goToBilling = () => { setOpen(false); navigate('/admin/billing'); };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
        title="Plan cart"
      >
        <ShoppingCart className="h-5 w-5" />
        {cartItems.length > 0 && (
          <span className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] px-0.5 rounded-full bg-indigo-600 text-white text-[10px] font-bold flex items-center justify-center">
            {cartItems.length}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-elevated border border-slate-100 py-2 animate-fade-in">
          <div className="px-4 py-2 border-b border-slate-100">
            <p className="text-sm font-semibold text-slate-800">Plan Cart</p>
          </div>
          {cartItems.length === 0 ? (
            <p className="px-4 py-4 text-sm text-slate-400 text-center">No plans added yet</p>
          ) : (
            <div className="px-4 py-2 space-y-1.5 max-h-64 overflow-y-auto">
              {cartItems.map((item) => (
                <div key={item.plan_tier} className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">{item.tier_label} × {item.quantity}</span>
                  <span className="text-slate-900 font-medium">{formatMoney(item.subtotal)}</span>
                </div>
              ))}
              <div className="pt-2 mt-1 border-t border-slate-100 space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Subtotal</span>
                  <span className="text-slate-700">{formatMoney(cartSubtotal)}</span>
                </div>
                {discountPercent > 0 && (
                  <div className="flex items-center justify-between text-sm text-emerald-600">
                    <span>Discount ({discountPercent}%)</span>
                    <span>-{formatMoney(cartSubtotal - cartTotal)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between text-sm font-semibold">
                  <span className="text-slate-800">Total</span>
                  <span className="text-slate-900">{formatMoney(cartTotal)}</span>
                </div>
              </div>
            </div>
          )}
          <div className="px-4 pt-2">
            <button onClick={goToBilling} className="w-full text-center text-xs font-medium px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
              {cartItems.length === 0 ? 'Choose a Plan' : 'Go to Billing'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const Navbar = ({ onMobileMenuToggle }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getBreadcrumb = () => {
    const parts = location.pathname.split('/').filter(Boolean);
    return parts.map((part, i) => ({
      label: part.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
      path: '/' + parts.slice(0, i + 1).join('/'),
    }));
  };

  const breadcrumbs = getBreadcrumb();

  const getProfilePath = () => {
    const role = user?.role;
    if (role === 'student') return '/student/profile';
    if (role === 'teacher') return '/teacher/profile';
    if (role === 'admin') return '/admin/profile';
    if (role === 'superadmin') return '/superadmin/profile';
    return '/';
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const handleLogout = () => {
    logout();
    window.location.href = SITE_HOME;
  };

  return (
    <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between sticky top-0 z-30 shadow-sm">
      {/* Left: Mobile menu + Breadcrumb */}
      <div className="flex items-center gap-3">
        <button
          onClick={onMobileMenuToggle}
          className="lg:hidden p-2 rounded-lg text-slate-500 hover:bg-slate-100"
        >
          <Menu className="h-5 w-5" />
        </button>

        {/* Breadcrumb */}
        <nav className="hidden sm:flex items-center gap-1 text-sm">
          <Link to="/" className="text-slate-400 hover:text-slate-600">
            <Home className="h-4 w-4" />
          </Link>
          {breadcrumbs.map((crumb, i) => (
            <React.Fragment key={crumb.path}>
              <span className="text-slate-300">/</span>
              {i === breadcrumbs.length - 1 ? (
                <span className="text-slate-700 font-medium">{crumb.label}</span>
              ) : (
                <Link
                  to={crumb.path}
                  className="text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {crumb.label}
                </Link>
              )}
            </React.Fragment>
          ))}
        </nav>
      </div>

      {/* Right: Cart (admin only) + Notifications + User */}
      <div className="flex items-center gap-3">
        <CartWidget />

        {/* Notification Bell */}
        <button className="relative p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors">
          <Bell className="h-5 w-5" />
          <span className="absolute top-1.5 right-1.5 h-2 w-2 bg-red-500 rounded-full" />
        </button>

        {/* User Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <div className="h-8 w-8 rounded-full bg-primary-600 flex items-center justify-center">
              <span className="text-white text-xs font-bold">
                {getInitials(user?.name || user?.username)}
              </span>
            </div>
            <div className="hidden sm:block text-left">
              <p className="text-sm font-medium text-slate-700 leading-tight">
                {user?.name || user?.username || 'User'}
              </p>
              <p className="text-xs text-slate-400 capitalize">{user?.role || ''}</p>
            </div>
            <ChevronDown className="h-4 w-4 text-slate-400 hidden sm:block" />
          </button>

          {/* Dropdown Menu */}
          {dropdownOpen && (
            <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-elevated border border-slate-100 py-1 animate-fade-in">
              <div className="px-4 py-3 border-b border-slate-100">
                <p className="text-sm font-semibold text-slate-800">{user?.name || user?.username}</p>
                <p className="text-xs text-slate-400">{user?.email}</p>
              </div>

              <Link
                to={getProfilePath()}
                onClick={() => setDropdownOpen(false)}
                className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-800 transition-colors"
              >
                <User className="h-4 w-4" />
                My Profile
              </Link>

              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
              >
                <LogOut className="h-4 w-4" />
                Sign Out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Navbar;
